/* dfb793af: pure view model for the Bambu Lab cabinet UI — status
   normalization, RU labels, badge classes, connect-form validation,
   token-expiry countdown and the connection/printer view builders.
   No React, no fetch: mirrored by tests/bambuView.test.ts on the
   node test runner (same pattern as nalogView). */

export type BambuConnectionStatus = 'active' | 'pendingVerification' | 'disconnected' | 'error';

/**
 * ProviderBambuService serializes Status.ToString() → "Active" /
 * "PendingVerification" / "Disconnected" / "Error"; ordinals stay
 * accepted defensively (enum drift). Unknown values degrade to
 * error — never a crash.
 */
export function normalizeBambuConnectionStatus(status: string | number): BambuConnectionStatus {
  if (typeof status === 'string') {
    const s = status.toLowerCase();
    if (s === 'active') return 'active';
    if (s === 'pendingverification') return 'pendingVerification';
    if (s === 'disconnected') return 'disconnected';
    return 'error';
  }
  // 0 Active, 1 Disconnected, 2 Error, 3 PendingVerification — ProviderBambuConnectionStatus order.
  return status === 0 ? 'active' : status === 1 ? 'disconnected' : status === 3 ? 'pendingVerification' : 'error';
}

export const BAMBU_CONNECTION_LABELS: Record<BambuConnectionStatus, string> = {
  active: 'Активен',
  pendingVerification: 'Ожидает кода',
  disconnected: 'Отключён',
  error: 'Ошибка',
};

export function bambuConnectionBadgeClass(status: BambuConnectionStatus): string {
  switch (status) {
    case 'active': return 'badge badge-success';
    case 'pendingVerification': return 'badge badge-warning';
    case 'disconnected': return 'badge badge-neutral';
    default: return 'badge badge-danger';
  }
}

/* ── Connect-form validation (mirrors ProviderBambuService rules) ── */

/** Same envelope as NormalizeAccount: 3–256 chars containing '@'. */
export function isValidBambuAccount(input: string): boolean {
  const account = input.trim();
  return account.length >= 3 && account.length <= 256 && account.includes('@');
}

/** Password is used for the single login call and never stored: ≥6 chars. */
export function isValidBambuPassword(input: string): boolean {
  return input.trim().length >= 6;
}

/** a958628d: the emailed 2FA code is exactly 6 digits (mirrors VerifyTfaAsync). */
export function isValidBambuTfaCode(input: string): boolean {
  return /^\d{6}$/.test(input.trim());
}

/* ── Token-expiry countdown (server already applies a 5-min skew) ── */

export type BambuTokenNoticeLevel = 'expired' | 'soon' | null;

export interface BambuTokenNotice {
  level: BambuTokenNoticeLevel;
  /** Ready-to-render RU line; null when nothing to surface. */
  text: string | null;
}

/** Days left until the token expires, rounded down; <0 when past. */
export function bambuTokenDaysLeft(tokenExpiresAtUtc: string | null | undefined, now: Date): number | null {
  if (!tokenExpiresAtUtc) return null;
  const expires = new Date(tokenExpiresAtUtc).getTime();
  if (Number.isNaN(expires)) return null;
  return Math.floor((expires - now.getTime()) / 86_400_000);
}

export function buildBambuTokenNotice(
  tokenExpired: boolean,
  tokenExpiresAtUtc: string | null | undefined,
  now: Date,
): BambuTokenNotice {
  if (tokenExpired) {
    return { level: 'expired', text: 'Срок действия токена истёк — переподключите аккаунт, чтобы управлять принтерами.' };
  }
  const days = bambuTokenDaysLeft(tokenExpiresAtUtc, now);
  if (days === null) return { level: null, text: null };
  if (days < 0) {
    // Server skew missed it but the wall clock already passed expiry.
    return { level: 'expired', text: 'Срок действия токена истёк — переподключите аккаунт, чтобы управлять принтерами.' };
  }
  if (days === 0) return { level: 'soon', text: 'Токен Bambu истекает сегодня — скоро понадобится переподключение.' };
  if (days <= 7) return { level: 'soon', text: `Токен Bambu истекает через ${days} дн. — переподключите, чтобы не потерять доступ.` };
  return { level: null, text: null };
}

/* ── Connection-card view builder ── */

export interface BambuCardView {
  status: BambuConnectionStatus;
  statusLabel: string;
  badgeClass: string;
  /** Human lines for the card body: «Аккаунт», dates, sync time. */
  lines: { label: string; value: string }[];
  tokenNotice: BambuTokenNotice;
  /** Only the error status surfaces LastError (API is token-free by contract). */
  problemText: string | null;
  /** a958628d: PendingVerification — render the 6-digit code-entry form. */
  needsVerification: boolean;
  /** Reconnect while inactive or when the token is dead/nearly dead. */
  canReconnect: boolean;
  canDisconnect: boolean;
  /** The printers panel needs a live token: active and not expired. */
  showPrinters: boolean;
}

export function buildBambuCardView(
  connection: {
    accountName: string;
    status: string | number;
    connectedAtUtc: string;
    tokenExpiresAtUtc?: string | null;
    tokenExpired: boolean;
    lastPrinterSyncAtUtc?: string | null;
    lastError?: string | null;
  },
  now: Date,
  formatDate: (iso: string) => string,
  formatDateTime: (iso: string) => string,
): BambuCardView {
  const status = normalizeBambuConnectionStatus(connection.status);
  const tokenNotice = buildBambuTokenNotice(connection.tokenExpired, connection.tokenExpiresAtUtc, now);
  const lines: { label: string; value: string }[] = [
    { label: 'Аккаунт', value: connection.accountName },
  ];
  if (status === 'disconnected') {
    lines.push({ label: 'Подключён', value: formatDate(connection.connectedAtUtc) });
  } else {
    lines.push({ label: 'Подключён', value: formatDate(connection.connectedAtUtc) });
    if (connection.tokenExpiresAtUtc) {
      lines.push({ label: 'Токен до', value: formatDateTime(connection.tokenExpiresAtUtc) });
    }
    if (connection.lastPrinterSyncAtUtc) {
      lines.push({ label: 'Синхронизация', value: formatDateTime(connection.lastPrinterSyncAtUtc) });
    }
  }
  const tokenTrouble = tokenNotice.level === 'expired';
  return {
    status,
    statusLabel: BAMBU_CONNECTION_LABELS[status],
    badgeClass: bambuConnectionBadgeClass(status),
    lines,
    tokenNotice,
    problemText: status === 'error' && connection.lastError ? connection.lastError : null,
    needsVerification: status === 'pendingVerification',
    // Pending offers "Переподключить" too: a fresh login re-sends the email code
    // (the challenge handle expires after 10 minutes server-side).
    canReconnect: status !== 'active' || tokenTrouble,
    canDisconnect: status === 'active',
    showPrinters: status === 'active' && !tokenTrouble,
  };
}

/* ── Printer-row view builder ── */

export interface BambuPrinterRowView {
  /** Name falls back to devId — same rule as the backend mapper. */
  name: string;
  modelLabel: string | null;
  online: boolean;
  onlineLabel: string;
  /** Dot marker class next to the printer name. */
  dotClass: string;
  devIdTail: string | null;
}

export function buildBambuPrinterRowView(printer: {
  devId: string;
  name: string;
  model: string;
  online: boolean;
}): BambuPrinterRowView {
  const model = printer.model.trim();
  return {
    name: printer.name.trim() || printer.devId,
    modelLabel: model ? model : null,
    online: printer.online,
    onlineLabel: printer.online ? 'онлайн' : 'офлайн',
    dotClass: printer.online ? 'bambu-dot bambu-dot-online' : 'bambu-dot bambu-dot-offline',
    devIdTail: printer.devId.length > 10 ? printer.devId.slice(-6) : printer.devId,
  };
}

/** Count of online printers for the panel caption. */
export function bambuOnlineCount(printers: { online: boolean }[]): number {
  return printers.filter((p) => p.online).length;
}
